import { useState } from 'react';
import ArrowLink from '../components/ArrowLink.jsx';
import PageHero from '../components/PageHero.jsx';
import SectionHeader from '../components/SectionHeader.jsx';
import { boardProfiles, leadershipGroups } from '../organization.js';

function Portrait({ person }) {
  return (
    <img
      src={person.image}
      alt={person.imageAlt}
      width={person.imageWidth}
      height={person.imageHeight}
      loading="lazy"
      decoding="async"
      style={{ objectPosition: person.position }}
    />
  );
}

function Biography({ person, compact = false }) {
  return (
    <div className={compact ? 'profile-biography profile-biography--compact' : 'profile-biography'}>
      <div className="profile-biography__header">
        <p className="profile-biography__eyebrow">Full biography</p>
        <h3>{person.name}</h3>
        <p className="profile-biography__role">{person.role}</p>
        <p className="profile-biography__descriptor">{person.descriptor}</p>
      </div>
      <div className="profile-biography__body">
        {person.bio.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        {person.quote ? <blockquote>{person.quote}</blockquote> : null}
        {person.links?.length ? (
          <div className="profile-biography__links">
            {person.links.map((link) => (
              <a href={link.href} key={link.href} target={link.href.startsWith('http') ? '_blank' : undefined} rel={link.href.startsWith('http') ? 'noreferrer' : undefined}>
                {link.label}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LeadershipGroup({ group, tinted }) {
  const [activeId, setActiveId] = useState(group.members[0].id);
  const [expandedId, setExpandedId] = useState(null);
  const activePerson = group.members.find((person) => person.id === activeId) || group.members[0];

  function selectPerson(person, { toggle = false } = {}) {
    setActiveId(person.id);
    if (toggle) setExpandedId((current) => current === person.id ? null : person.id);
  }

  return (
    <section id={group.id} className={`leadership-group section-pad ${tinted ? 'section-tint' : ''}`}>
      <div className="shell">
        <SectionHeader label={group.eyebrow} title={group.title} body={group.description} />
        <p className="leadership-group__instruction">Hover, focus, or select a portrait to read the full biography.</p>
        <div className="leadership-roster">
          <div className="leadership-grid">
            {group.members.map((person) => {
              const isActive = activePerson.id === person.id;
              const isExpanded = expandedId === person.id;

              return (
                <article className={`leadership-card ${isActive ? 'leadership-card--active' : ''} ${isExpanded ? 'leadership-card--expanded' : ''}`} id={person.id} key={person.id}>
                  <div className="leadership-card__summary">
                    <button
                      type="button"
                      className="leadership-card__button"
                      aria-pressed={isActive}
                      aria-expanded={isExpanded}
                      aria-controls={`${person.id}-mobile-biography`}
                      aria-describedby={`${person.id}-card-role ${person.id}-card-description`}
                      onPointerEnter={(event) => {
                        if (event.pointerType === 'mouse') selectPerson(person);
                      }}
                      onFocus={() => selectPerson(person)}
                      onClick={() => selectPerson(person, { toggle: true })}
                    >
                      <span className="sr-only">Show the full biography for {person.name}</span>
                    </button>
                    <figure className="leadership-card__portrait">
                      <Portrait person={person} />
                      <figcaption>Read full biography</figcaption>
                    </figure>
                    <div className="leadership-card__content">
                      <h3>{person.name}</h3>
                      <p className="leadership-card__role" id={`${person.id}-card-role`}>{person.role}</p>
                      <p className="leadership-card__descriptor" id={`${person.id}-card-description`}>{person.descriptor}</p>
                    </div>
                  </div>
                  <div
                    id={`${person.id}-mobile-biography`}
                    className={`leadership-card__mobile-biography ${isExpanded ? 'is-open' : ''}`}
                    hidden={!isExpanded}
                  >
                    <Biography person={person} compact />
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="leadership-reader" aria-label={`Full biography for ${activePerson.name}`}>
            <p className="sr-only" aria-live="polite">Showing the full biography for {activePerson.name}.</p>
            <div className="leadership-reader__portrait" aria-hidden="true">
              <Portrait person={activePerson} />
            </div>
            <div className="leadership-reader__scroll" key={activePerson.id}>
              <Biography person={activePerson} />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default function Board() {
  return (
    <>
      <PageHero
        label="Board & leadership"
        title="Purpose needs thoughtful stewardship."
        intro={`Meet the ${boardProfiles.length} board leaders, advisors, program directors, community advocates, and visionary partners guiding K.E.E.N.’s mission with accountability, collaboration, and care.`}
      >
        <div className="button-row">
          <a className="button button--gold" href="#board-leadership">Meet the leadership</a>
          <ArrowLink href="/our-story">Read the founder story</ArrowLink>
        </div>
      </PageHero>

      <section className="board-introduction section-pad">
        <div className="shell">
          <SectionHeader
            label="Organizational leadership"
            title="Many disciplines. One shared purpose."
            body="K.E.E.N.’s leadership brings together board governance, strategic advisors, program direction, cultural stewardship, wellness expertise, communications, and community partnership."
          />
          <div className="board-introduction__note">
            <span>Biography edition</span>
            <p>This roster reflects K.E.E.N.’s complete leadership biography packet, including the newest board, advisory, program, committee, and community-partner profiles.</p>
          </div>
        </div>
      </section>

      {leadershipGroups.map((group, groupIndex) => (
        <LeadershipGroup group={group} tinted={groupIndex % 2 === 0} key={group.id} />
      ))}

      <section className="partner-cta section-pad">
        <div className="shell narrow partner-cta__inner">
          <p className="overline">Connect with the board</p>
          <h2>Leadership grounded in <em>shared purpose.</em></h2>
          <p>Connect with K.E.E.N. about governance, community partnerships, program collaboration, or opportunities to support the movement.</p>
          <div className="button-row button-row--center">
            <a className="button button--gold" href="/contact">Contact the team</a>
            <ArrowLink href="/partner" inverse>Explore partnerships</ArrowLink>
          </div>
        </div>
      </section>
    </>
  );
}
